import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { get, isNil } from 'lodash';
import PropTypes from 'prop-types';

import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction } from 'actions/workflow';
import { FormRow } from 'components/Form';
import { Button, Toggle } from 'components/Button';
import InfoTooltip from 'components/InfoTooltip';
import GeneralError from 'components/GeneralError';

const AutoPay = ({
  navigate,
  searchParams,
  dispatch,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [isAutoPay, setIsAutoPay] = useState(false);

  useEffect(() => {
    if (!isNil(checkinData) && !isNil(get(checkinData, 'isAutoPay'))) {
      setIsAutoPay(get(checkinData, 'isAutoPay'));
    }
  }, [checkinData]);

  const handleNext = (e) => {
    e.preventDefault();
    setGeneralError(null);
    setIsSubmitting(true);

    dispatch(nextAction({
      key: searchParams.get('key'),
      data: { isAutoPay },
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: (err) => {
        setGeneralError(get(err, 'data.failure.errors.0') || { message: 'An error occurred. Please, try again.' });
        setIsSubmitting(false);
      },
    }));
  };

  return (
    <div className="flex flex-col mb-2 mt-10">
      <div className="flex flex-col gap-6 w-full mt-4 mb-8">
        <div className="">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold">Set Up Auto-Pay</h2>
                <InfoTooltip
                  position="bottom"
                  title="What is Auto-Pay?"
                  tooltip={(
                    <div className="md:w-[300px] whitespace-normal my-2">
                      <p className="text-sm mb-4">Auto-Pay automatically deducts your scheduled loan payments from your linked funding account on the due date.</p>

                      <h5 className="font-semibold mb-2">Benefits</h5>
                      <ul className="list-disc pl-6">
                        <li>
                          <span className="font-semibold">Earn a 0.25% interest rate discount</span>
                          {' '}
                          when you enroll in Auto-Pay if you also verified your bank account only through our instant verification system. Please note that you may lose your 0.25% promotional interest rate reduction if you unenroll from Auto-Pay during your loan term. The 0.25% promotional interest rate reduction is
                          {' '}
                          <span className="underline">not available</span>
                          {' '}
                          for the "No Monthly Payment" repayment option.
                        </li>
                        <li>
                          <span className="font-semibold">Avoid missed payments</span>
                          {' '}
                          – Payments are made automatically, reducing the chance of late fees or collateral liquidation. 
                        </li>
                      </ul>
                    </div>
                  )}
                  hoverColor="blue-700"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{!isAutoPay ? 'Not' : ''} Enrolled</span>
                <Toggle checked={isAutoPay} onChange={(val) => {
                  setIsAutoPay(val);
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="font-semibold mb-5">Your Payment Account</h2>
      <div className="flex flex-wrap mb-4 gap-y-4 md:gap-y-0">
        <div className="flex flex-col gap-2 py-3 px-4 md:px-6 md:pl-0 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
          <span className="font-light uppercase text-xs text-slate-800">Name on Account</span>
          <h5 className="text-sm font-semibold">{get(checkinData, 'fundingAccount.accountHolderName') || '-'}</h5>
        </div>
        <div className="flex flex-col gap-2 py-3 px-4 md:px-6 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
          <span className="font-light uppercase text-xs text-slate-800">Account Type</span>
          <h5 className="text-sm font-semibold">{get(checkinData, 'fundingAccount.accountType') || '-'}</h5>
        </div>
        <div className="flex flex-col gap-2 py-3 px-4 md:px-6 border-l md:border-0 md:border-r border-gray-200 w-1/2 md:w-3/12">
          <span className="font-light uppercase text-xs text-slate-800">Routing Number</span>
          <h5 className="text-sm font-semibold">{get(checkinData, 'fundingAccount.routingNumber') || '-'}</h5>
          <span className="text-xs text-blue-500 font-normal">{get(checkinData, 'fundingAccount.bankName')}</span>
        </div>
        <div className="flex flex-col gap-2 py-3 px-4 md:pl-6 w-1/2 border-l md:border-0 border-gray-200 md:w-3/12">
          <span className="font-light uppercase text-xs text-slate-800">Account Number</span>
          <h5 className="text-sm font-semibold">{get(checkinData, 'fundingAccount.accountNumber') || '-'}</h5>
        </div>
      </div>

      <GeneralError error={generalError} className="w-full mb-4" />

      <FormRow>
        <Button
          className="w-full"
          label="Continue to Sign Contract"
          onClick={handleNext}
          isLoading={isSubmitting}
        />
      </FormRow>

      <FormRow className="border-t border-gray-200 pt-6 mt-4 !mb-0">
        <p className="text-xs text-gray-500 leading-relaxed pr-4">
          <span className="font-semibold text-gray-600">Please Note: </span>
          Enrolling in Auto-Pay is optional. The Bank's credit decision is not dependent on whether you enroll in Auto-Pay. You can change your method of payment at any time in the future.
        </p>
      </FormRow>
    </div>
  );
};

AutoPay.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
};

export default AutoPay;
